import styled, { css, keyframes } from 'styled-components';

export const Card = styled.div`
  background: #ffffff;
  border: 1px solid #eef2f7;
  border-radius: 14px;
  overflow: hidden;
  font-family: 'Plus Jakarta Sans', sans-serif;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    box-shadow: 0 4px 14px rgba(15, 23, 42, 0.04);
  }
`;

export const CardHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  padding: 20px 22px 16px;
  border-bottom: 1px solid #eef2f7;

  .titles h3 {
    margin: 0;
    font-size: 17px;
    font-weight: 700;
    color: #0f172a;
    letter-spacing: -0.02em;
  }

  .titles .subtitle {
    display: block;
    font-size: 12px;
    color: #64748b;
    margin-top: 4px;
    font-weight: 500;
  }

  .actions {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
    justify-content: flex-end;
  }
`;

export const FiltersBar = styled.form`
  display: grid;
  grid-template-columns: repeat(12, minmax(0, 1fr));
  gap: 12px;
  padding: 16px 22px;
  border-bottom: 1px solid #eef2f7;
  background: #fbfcfe;
`;

interface FilterFieldProps {
  $span?: number;
}

export const FilterFieldWrap = styled.div<FilterFieldProps>`
  grid-column: span ${({ $span }) => $span || 3};
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;

  label {
    font-size: 11px;
    font-weight: 600;
    color: #64748b;
    letter-spacing: 0.04em;
    text-transform: uppercase;
  }

  @media (max-width: 992px) {
    grid-column: span 6;
  }

  @media (max-width: 576px) {
    grid-column: span 12;
  }
`;

const inputBase = css`
  width: 100%;
  height: 38px;
  border-radius: 10px;
  border: 1px solid #e2e8f0;
  background: #ffffff;
  padding: 0 12px;
  font-size: 13px;
  color: #0f172a;
  font-family: inherit;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;

  &::placeholder {
    color: #94a3b8;
  }

  &:focus {
    border-color: #006633;
    box-shadow: 0 0 0 3px rgba(0, 102, 51, 0.12);
  }
`;

export const TextInput = styled.input`
  ${inputBase}
`;

export const SelectInput = styled.select`
  ${inputBase}
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%2364748b' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 10px center;
  padding-right: 32px;
`;

export const FilterActions = styled.div`
  grid-column: span 12;
  display: flex;
  justify-content: flex-end;
  gap: 8px;
`;

const buttonBase = css`
  height: 36px;
  padding: 0 14px;
  border-radius: 10px;
  font-size: 13px;
  font-weight: 600;
  border: 1px solid transparent;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease, border-color 0.15s ease;
  font-family: inherit;
`;

export const GhostButton = styled.button`
  ${buttonBase}
  background: #ffffff;
  color: #334155;
  border-color: #e2e8f0;

  &:hover {
    background: #f8fafc;
    border-color: #cbd5e1;
  }
`;

export const PrimaryButton = styled.button`
  ${buttonBase}
  background: #006633;
  color: #ffffff;

  &:hover {
    background: #00451f;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

export const TableWrapper = styled.div`
  overflow-x: auto;
`;

interface TableProps {
  $sticky?: boolean;
}

export const Table = styled.table<TableProps>`
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  font-size: 13px;

  thead th {
    text-align: left;
    font-size: 11px;
    font-weight: 700;
    color: #64748b;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    padding: 12px 16px;
    background: #f8fafc;
    border-bottom: 1px solid #eef2f7;
    white-space: nowrap;

    ${({ $sticky }) =>
      $sticky &&
      css`
        position: sticky;
        top: 0;
        z-index: 1;
      `}
  }

  tbody td {
    padding: 14px 16px;
    color: #334155;
    border-bottom: 1px solid #f1f5f9;
    vertical-align: middle;
  }

  tbody tr {
    transition: background 0.12s ease;
  }

  tbody tr:hover {
    background: #fbfcfe;
  }

  tbody tr:last-child td {
    border-bottom: none;
  }

  tbody tr.clickable {
    cursor: pointer;
  }
`;

interface CellAlignProps {
  $align?: 'left' | 'center' | 'right';
}

export const Th = styled.th<CellAlignProps>`
  text-align: ${({ $align }) => $align || 'left'} !important;
`;

export const Td = styled.td<CellAlignProps>`
  text-align: ${({ $align }) => $align || 'left'};
`;

export const SortButton = styled.button<{ $active?: boolean }>`
  background: none;
  border: none;
  padding: 0;
  font: inherit;
  color: inherit;
  letter-spacing: inherit;
  text-transform: inherit;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: ${({ $active }) => ($active ? '#006633' : 'inherit')};

  svg {
    opacity: ${({ $active }) => ($active ? 1 : 0.5)};
  }

  &:hover {
    color: #006633;
  }
`;

export const EmptyState = styled.div`
  padding: 56px 24px;
  text-align: center;
  color: #94a3b8;
  font-size: 13px;
  font-weight: 500;

  .title {
    color: #475569;
    font-weight: 600;
    font-size: 14px;
    margin-bottom: 4px;
  }
`;

export const Footer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 14px 22px;
  border-top: 1px solid #eef2f7;
  background: #fbfcfe;
  font-size: 12px;
  color: #64748b;
  font-weight: 500;

  .count strong {
    color: #0f172a;
    font-weight: 700;
  }

  @media (max-width: 576px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

export const Pager = styled.nav`
  display: flex;
  align-items: center;
  gap: 4px;
`;

interface PageButtonProps {
  $active?: boolean;
}

export const PageButton = styled.button<PageButtonProps>`
  min-width: 32px;
  height: 32px;
  padding: 0 10px;
  border-radius: 8px;
  border: 1px solid ${({ $active }) => ($active ? '#006633' : '#e2e8f0')};
  background: ${({ $active }) => ($active ? '#006633' : '#ffffff')};
  color: ${({ $active }) => ($active ? '#ffffff' : '#334155')};
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease;
  font-variant-numeric: tabular-nums;

  &:hover:not(:disabled) {
    border-color: #006633;
    color: ${({ $active }) => ($active ? '#ffffff' : '#006633')};
  }

  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }
`;

export const PageEllipsis = styled.span`
  padding: 0 6px;
  color: #94a3b8;
  font-weight: 600;
  user-select: none;
`;

const shimmer = keyframes`
  0% { background-position: -400px 0; }
  100% { background-position: 400px 0; }
`;

export const SkeletonRow = styled.div`
  height: 14px;
  border-radius: 6px;
  background: linear-gradient(90deg, #f1f5f9 0%, #e2e8f0 50%, #f1f5f9 100%);
  background-size: 800px 100%;
  animation: ${shimmer} 1.2s ease-in-out infinite;
`;

export const ActiveFilters = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  padding: 10px 22px 0;
`;

export const Chip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: #e6f3ec;
  color: #006633;
  font-size: 12px;
  font-weight: 600;
  padding: 4px 8px 4px 10px;
  border-radius: 999px;

  button {
    background: none;
    border: none;
    color: inherit;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    padding: 0;
    opacity: 0.7;

    &:hover {
      opacity: 1;
    }
  }
`;
